
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { SidebarProvider } from "@/components/ui/sidebar";
import ProductCopilotInterface from "@/components/product-copilot/ProductCopilotInterface";
import ProductCopilotSidebar from "@/components/product-copilot/ProductCopilotSidebar";
import ProductTemplateSelector from "@/components/product-copilot/ProductTemplateSelector";
import { PenLine, Plus, History, Lightbulb, BarChart, MessageSquare, CircleHelp, Star } from "lucide-react";

// Define category structure
interface Category {
  id: string;
  name: string;
  icon: string;
}

const ProductCopilotPage = () => {
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session');
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeCategory, setActiveCategory] = useState<string>("prd"); // Default to PRD category
  const [showTemplateGrid, setShowTemplateGrid] = useState<boolean>(!sessionIdParam);
  
  // Define categories 
  const categories: Category[] = [
    { id: 'prd', name: 'Product Requirements', icon: 'file-text' },
    { id: 'brainstorm', name: 'Feature Brainstorming', icon: 'lightbulb' },
    { id: 'research', name: 'Market Research', icon: 'bar-chart' },
    { id: 'voc', name: 'Customer Insights', icon: 'message-square' },
  ];
  
  useEffect(() => {
    // Set loading to false after a short delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    // If we have a session ID, we don't need to select a template
    if (sessionIdParam) {
      setSelectedPrompt(null);
      setShowTemplateGrid(false);
    }
  }, [sessionIdParam]);
  
  const handleNewChat = () => {
    navigate('/product-copilot');
    setSelectedPrompt(null);
    setShowTemplateGrid(true);
  };
  
  const handlePromptSelect = (promptId: string) => {
    setSelectedPrompt(promptId);
    setShowTemplateGrid(false);
  };
  
  // Redirect non-admin users
  if (!isLoading && (!user || !isAdmin)) {
    toast({
      title: "Access denied",
      description: "You need admin privileges to access this page.",
      variant: "destructive",
    });
    return <Navigate to="/" replace />;
  }
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="flex-1 pt-24 pb-16 px-0 sm:px-0 lg:px-0">
        <SidebarProvider>
          <div className="flex min-h-[600px] max-w-screen-2xl mx-auto">
            <ProductCopilotSidebar
              onPromptSelect={handlePromptSelect}
              selectedPrompt={selectedPrompt}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
            />
            
            <div className="flex-1 p-4">
              {showTemplateGrid ? (
                <div className="h-full flex flex-col">
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-2">
                      <h1 className="text-3xl font-bold">Product Copilot</h1>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          onClick={() => navigate('/chat-history')}
                          size="sm"
                        >
                          <History className="h-4 w-4 mr-2" />
                          Chat History
                        </Button>
                        <Button 
                          variant="default"
                          onClick={handleNewChat}
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          New Chat
                        </Button>
                      </div>
                    </div>
                    <p className="text-muted-foreground">
                      Select from our collection of product management templates
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-8">
                    {activeCategory === "prd" && (
                      <section>
                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                          <PenLine className="mr-2 h-5 w-5 text-blue-500" /> 
                          Product Requirements
                        </h2>
                        <ProductTemplateSelector 
                          onPromptSelect={handlePromptSelect} 
                          selectedPrompt={selectedPrompt}
                          filterType="recent"
                          categoryFilter="prd"
                          displayType="grid"
                        />
                      </section>
                    )}
                    
                    {activeCategory === "brainstorm" && (
                      <section>
                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                          <Lightbulb className="mr-2 h-5 w-5 text-amber-500" /> 
                          Feature Brainstorming
                        </h2>
                        <ProductTemplateSelector 
                          onPromptSelect={handlePromptSelect} 
                          selectedPrompt={selectedPrompt}
                          filterType="recent"
                          categoryFilter="brainstorm"
                          displayType="grid"
                        />
                      </section>
                    )}
                    
                    {activeCategory === "research" && (
                      <section>
                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                          <BarChart className="mr-2 h-5 w-5 text-orange-500" /> 
                          Market Research
                        </h2>
                        <ProductTemplateSelector 
                          onPromptSelect={handlePromptSelect} 
                          selectedPrompt={selectedPrompt}
                          filterType="recent"
                          categoryFilter="research"
                          displayType="grid"
                        />
                      </section>
                    )}
                    
                    {activeCategory === "voc" && (
                      <section>
                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                          <MessageSquare className="mr-2 h-5 w-5 text-green-500" /> 
                          Customer Insights
                        </h2>
                        <ProductTemplateSelector 
                          onPromptSelect={handlePromptSelect} 
                          selectedPrompt={selectedPrompt}
                          filterType="recent"
                          categoryFilter="voc"
                          displayType="grid"
                        />
                      </section>
                    )}
                    
                    {activeCategory === "favorites" && (
                      <section>
                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                          <Star className="mr-2 h-5 w-5 text-amber-400" /> 
                          Favorite Templates
                        </h2>
                        <ProductTemplateSelector 
                          onPromptSelect={handlePromptSelect} 
                          selectedPrompt={selectedPrompt}
                          filterType="favorites"
                          displayType="grid"
                        />
                      </section>
                    )}
                    
                    {activeCategory !== "prd" && 
                     activeCategory !== "brainstorm" && 
                     activeCategory !== "research" && 
                     activeCategory !== "voc" && 
                     activeCategory !== "favorites" && 
                     activeCategory !== "history" && (
                      <section>
                        <h2 className="text-xl font-semibold mb-4 flex items-center">
                          <CircleHelp className="mr-2 h-5 w-5" /> 
                          {categories.find(c => c.id === activeCategory)?.name || "Templates"}
                        </h2>
                        <ProductTemplateSelector 
                          onPromptSelect={handlePromptSelect} 
                          selectedPrompt={selectedPrompt}
                          filterType="recent"
                          categoryFilter={activeCategory}
                          displayType="grid"
                        />
                      </section>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-border/40 overflow-hidden h-[600px] flex flex-col">
                  <ProductCopilotInterface 
                    promptId={selectedPrompt} 
                    onClearPrompt={() => {
                      setSelectedPrompt(null);
                      setShowTemplateGrid(true);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </SidebarProvider>
      </div>
      
      <Footer />
    </div>
  );
};

export default ProductCopilotPage;
